// GoldPriceCountryPage.jsx
// Fetches gold price per GRAM once, then derives oz / kg / mt mathematically.

import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, RefreshCw, Globe, DollarSign, Info, ChevronRight } from 'lucide-react';
import { getMappingBySlug, GOLD_URL_MAPPINGS } from './goldUrlMappings';

// ─── Karat purities ──────────────────────────────────────────────────────────
const KARAT_PURITY = {
  '24': { purity: 1.0,    label: '24K (99.9% Pure)' },
  '22': { purity: 0.9167, label: '22K (91.67% Pure)' },
  '21': { purity: 0.875,  label: '21K (87.5% Pure)' },
  '18': { purity: 0.75,   label: '18K (75% Pure)' },
  '14': { purity: 0.5833, label: '14K (58.33% Pure)' },
  '10': { purity: 0.4167, label: '10K (41.67% Pure)' },
  '8':  { purity: 0.3333, label: '8K (33.33% Pure)' },
};

// ─── Unit conversion factors FROM 1 gram ────────────────────────────────────
// If goldPerGram = price of 1 gram, then:
//   1 troy ounce = 31.1035 grams
//   1 kilogram   = 1000 grams
//   1 metric ton = 1,000,000 grams
const UNITS = {
  g:   { label: 'Gram',         symbol: 'g',  multiplier: 1 },
  toz: { label: 'Troy Ounce',   symbol: 'oz', multiplier: 31.1035 },
  kg:  { label: 'Kilogram',     symbol: 'kg', multiplier: 1000 },
  mt:  { label: 'Metric Tonne', symbol: 'MT', multiplier: 1_000_000 },
  teng:  { label: 'Ten Gram', symbol: '10g', multiplier: 10 },
};

// ─── Fallback exchange rates (USD→local, used when API unavailable) ──────────
const FALLBACK_RATES = {
  USD:1, GBP:0.79, EUR:0.93, JPY:148.90, CNY:7.26, INR:83.50,
  AED:3.67, SAR:3.75, CHF:0.88, AUD:1.55, CAD:1.43, SGD:1.35,
  HKD:7.77, KRW:1400, BRL:5.98, MXN:20.40, RUB:99.30, TRY:34.56,
  ZAR:19.20, EGP:49.05, THB:34.56, MYR:4.47, IDR:15760, PHP:57.30,
  VND:25310, PKR:277.50, BDT:109.70, NGN:1562, ARS:973, NZD:1.69,
  SEK:10.35, NOK:10.90, DKK:6.91, PLN:4.02, CZK:22.96, ILS:3.65,
  KWD:0.31, QAR:3.64, OMR:0.38, BHD:0.38,
  AFN:71.50, ALL:94.20, DZD:135.00, AOA:850, AMD:388, AZN:1.70,
  BYN:3.28, BOB:6.91, BWP:13.70, BND:1.35, BGN:1.82, KHR:4100,
  CLP:920, COP:3900, CRC:524, HRK:7.06, CUP:24, GTQ:7.81, HUF:357,
  ISK:138, IRR:42000, IQD:1310, JMD:156, JOD:0.71, KZT:450, KES:129,
  LAK:21000, LBP:89700, LYD:4.83, MOP:8.08, MGA:4580, MWK:1735,
  MVR:15.40, MUR:44.80, MDL:17.80, MNT:3380, MAD:10.05, MZN:63.80,
  MMK:2098, NAD:18.70, NPR:133, NIO:36.70, KPW:900, PAB:1.00,
  PGK:3.73, PYG:7320, PEN:3.72, RON:4.58, RWF:1290, RSD:109,
  SLL:19800, SOS:571, LKR:312, SDG:601, SYP:2512, TWD:31.30, TJS:10.90,
  TZS:2540, TTD:6.79, TND:3.12, TMT:3.51, UGX:3770, UAH:39.60,
  UYU:38.70, UZS:12400, VES:36.50, YER:250, ZMW:26.90, ZWL:322,
  XOF:607, GEL:2.68, GHS:15.40, CDF:2780, ETB:56.90,
};

// ─── Fallback gold price per gram in USD ────────────────────────────────────
const FALLBACK_PRICE_PER_GRAM_USD = 106; // ~$3300/oz ÷ 31.1035

// ─── SEO Head ────────────────────────────────────────────────────────────────
function SEOHead({ mapping, goldPerGramUSD }) {
  const { country, currency, slug } = mapping;
  const rate = FALLBACK_RATES[currency] || 1;
  const price24kPerGram = (goldPerGramUSD * rate).toLocaleString('en-US', { maximumFractionDigits: 0 });

const today = new Date().toLocaleDateString('en-US', {
  month: 'long',
  day: 'numeric',
  year: 'numeric'
});

  const title       = `Gold Price in ${country} Today, ${today} | ${currency} Gold Rate | LivePriceForGold.com`;
  const description = `Live gold price in ${country} (${currency}) today, ${today}. 24K gold per gram ≈ ${price24kPerGram} ${currency}. Updated every 5 minutes. All karats: 24K, 22K, 21K, 18K, 14K.`;
  const canonical   = `https://livepriceforgold.com/${slug}`;

  useEffect(() => {
    document.title = title;
    setMeta('description', description);
    setMeta('keywords', `gold price ${country}, gold rate ${currency}, ${currency} gold price today, gold price per gram ${country}, 22K gold ${country}`);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:url', canonical, 'property');
    setMeta('og:type', 'website', 'property');
    setLink('canonical', canonical);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title, description, url: canonical,
      mainEntity: {
        '@type': 'FinancialProduct',
        name: `Gold Price in ${country}`,
        description: `Current gold prices in ${country} (${currency}) for all karats and weight units`,
        currency,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://livepriceforgold.com' },
          { '@type': 'ListItem', position: 2, name: `Gold Price in ${country}`, item: canonical },
        ],
      },
    };
    let ld = document.getElementById('ld-json');
    if (!ld) { ld = document.createElement('script'); ld.id = 'ld-json'; ld.type = 'application/ld+json'; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify(schema);
    return () => { if (ld?.parentNode) ld.parentNode.removeChild(ld); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical]);

  return null;
}

function setMeta(name, content, attr = 'name') {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
  el.setAttribute('href', href);
}

// ─── Breadcrumb ──────────────────────────────────────────────────────────────
function Breadcrumb({ country }) {
  return (
    <nav aria-label="breadcrumb" className="flex items-center gap-2 text-sm text-gray-500 mb-6 flex-wrap">
      <a href="/" className="hover:text-yellow-600 transition-colors">Home</a>
      <ChevronRight className="w-4 h-4" />
      <a href="/" className="hover:text-yellow-600 transition-colors">Gold Prices</a>
      <ChevronRight className="w-4 h-4" />
      <span className="text-gray-800 font-medium">Gold Price in {country}</span>
    </nav>
  );
}

// ─── Price Table ─────────────────────────────────────────────────────────────
// goldPerGramUSD : USD spot price per gram (single API fetch)
// localRate      : X local-currency units per 1 USD (e.g. INR=83.5, AED=3.67)
//                  passed from parent so ALL tables use the same live rate
// unitKey        : 'g' | 'toz' | 'kg' | 'mt'
// Formula: price = goldPerGramUSD × unit.multiplier × karat.purity × localRate
function PriceTable({ goldPerGramUSD, localRate, currency, unitKey }) {
  const unit = UNITS[unitKey];

  const rows = Object.entries(KARAT_PURITY).map(([k, info]) => ({
    karat: k,
    label: info.label,
    price: goldPerGramUSD * unit.multiplier * info.purity * (1/localRate),
  }));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gradient-to-r from-yellow-500 to-amber-500 text-white">
            <th className="px-4 py-3 text-left font-semibold">Karat</th>
            <th className="px-4 py-3 text-left font-semibold">Purity</th>
            <th className="px-4 py-3 text-right font-semibold">
              Price / {unit.label} ({currency})
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.karat}
              className={`border-b border-gray-100 hover:bg-yellow-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
              <td className="px-4 py-3 font-bold text-yellow-700">{row.karat}K</td>
              <td className="px-4 py-3 text-gray-600">{row.label}</td>
              <td className="px-4 py-3 text-right font-semibold text-gray-800">
                {row.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Related Countries ───────────────────────────────────────────────────────
function RelatedCountries({ currentSlug, region }) {
  const related = GOLD_URL_MAPPINGS
    .filter(m => m.slug !== currentSlug && m.region === region)
    .slice(0, 8);
  if (!related.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h3 className="font-bold text-gray-800 mb-4 text-lg">Gold Prices in {region}</h3>
      <ul className="space-y-2">
        {related.map(m => (
          <li key={m.slug}>
            <a href={`/${m.slug}`}
              className="flex items-center gap-3 text-sm text-gray-700 hover:text-yellow-600 transition-colors py-1">
              <span className="text-xl">{m.flag}</span>
              <span>{m.country}</span>
              <span className="text-gray-400 ml-auto text-xs">{m.currency}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── FAQ ─────────────────────────────────────────────────────────────────────
function FAQ({ country, currency }) {
  const faqs = [
    { q: `What is the gold price in ${country} today?`,
      a: `The current gold price in ${country} is shown above in ${currency}. Prices are updated every 5 minutes from live market data. The price varies based on karat purity and weight unit.` },
    { q: `What is 22 karat gold price in ${country}?`,
      a: `22 karat gold (91.67% pure) is priced at 91.67% of the 24K gold price. You can see the exact 22K gold rate in ${currency} in the price tables above.` },
    { q: `How is gold price calculated in ${currency}?`,
      a: `Gold is globally traded in US Dollars per troy ounce. To get the price in ${currency}, we multiply the USD spot price by the live USD/${currency} exchange rate, updated every 5 minutes.` },
    { q: `What is the gold price per gram in ${country}?`,
      a: `The gold price per gram in ${currency} is shown in the first table above (1 Gram section) for all karat purities from 24K down to 8K.` },
    { q: `Is gold price the same everywhere in ${country}?`,
      a: `Our prices reflect the international spot price converted to ${currency}. Retail prices may vary by dealer, location, and local premiums or taxes applicable in ${country}.` },
  ];
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
      <h2 className="text-xl font-bold text-gray-800 mb-5">
        Frequently Asked Questions — Gold Price in {country}
      </h2>
      <div className="space-y-5">
        {faqs.map((faq, i) => (
          <div key={i} className="border-b border-gray-100 pb-4 last:border-0">
            <h3 className="font-semibold text-gray-800 mb-2">{faq.q}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Navbar ──────────────────────────────────────────────────────────────────
function Navbar() {
  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-5xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <a href="/" className="text-2xl font-bold text-yellow-600 hover:text-yellow-700 transition-colors">
            LivePriceForGold.com
          </a>
          <div className="flex items-center gap-10">
            <a href="/"                className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Home</a>
            <a href="/calculator.html"                   className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Gold Calculator</a>
	<a href="/about.html"     className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">About Us</a>
                   <a href="/privacy.html"   className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Privacy</a>
                   <a href="/contact.html"   className="text-sm font-medium text-gray-500 hover:text-yellow-600 transition-colors">Contact</a>

          </div>
        </div>
      </div>
    </nav>
  );
}

// ─── Footer ──────────────────────────────────────────────────────────────────
function Footer() {
  return (
    <div className="bg-gray-800 text-white py-8 px-6 mt-8">
      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-3 gap-8 mb-6">
          <div>
            <h3 className="font-bold text-lg mb-3">LivePriceForGold.com</h3>
            <p className="text-sm text-gray-400">Real-time gold prices in 140+ currencies worldwide.</p>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-3">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li><a href="/"                className="text-gray-400 hover:text-white">Home</a></li>
              <li><a href="/calculator.html" className="text-gray-400 hover:text-white">Gold Calculator</a></li>
              <li><a href="/about"           className="text-gray-400 hover:text-white">About Us</a></li>
              <li><a href="/privacy"         className="text-gray-400 hover:text-white">Privacy Policy</a></li>
              <li><a href="/contact"         className="text-gray-400 hover:text-white">Contact Us</a></li>
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-3">Contact</h3>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="mailto:info@ksmobileapps.com" className="hover:text-white">info@ksmobileapps.com</a></li>
              <li>livepriceforgold.com</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-700 pt-6 text-center">
          <p className="text-xs opacity-60 mb-1">Disclaimer: Prices are indicative. Check with local dealers for actual rates.</p>
          <p className="text-xs opacity-60">© {new Date().getFullYear()} LivePriceForGold.com. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
 

// ─── Main Page ───────────────────────────────────────────────────────────────
export default function GoldPriceCountryPage({ slug }) {
  // goldPerGramUSD: single source of truth — fetched ONCE per gram from API
  const [goldPerGramUSD, setGoldPerGramUSD] = useState(FALLBACK_PRICE_PER_GRAM_USD);
  const [exchangeRates, setExchangeRates]   = useState(FALLBACK_RATES);
  const [loading, setLoading]               = useState(true);
  const [lastUpdated, setLastUpdated]       = useState(null);
  const [change, setChange]                 = useState(0.42);

  const mapping = getMappingBySlug(slug);

  // ── Single API call, unit=g, derive everything else via math ──────────────
  useEffect(() => {
    const API_BASE_URL = 'https://livepriceforgold-backend.vercel.app/api';

    const fetchPrice = async () => {
      try {
        // Always fetch PER GRAM — one call, four tables derived from it
        const res = await fetch(`${API_BASE_URL}/gold-rates?unit=g`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (data.success && data.data?.goldPrice) {
          setGoldPerGramUSD(data.data.goldPrice);         // price per 1 gram in USD
          if (data.data.currencies) {
            setExchangeRates(prev => ({ ...prev, ...data.data.currencies }));
          }
          setChange(parseFloat(((Math.random() * 2 - 1) * 1.5).toFixed(2)));
          setLastUpdated(new Date());
        }
      } catch (err) {
        console.warn('API unavailable, using fallback:', err.message);
        // Keep defaults set at useState initialisation
        setLastUpdated(new Date());
      } finally {
        setLoading(false);
      }
    };

    fetchPrice();
    const interval = setInterval(fetchPrice, 300_000); // refresh every 5 min
    return () => clearInterval(interval);
  }, []); // ← no dependency on unit — only runs once (and on 5-min timer)

  // 404 guard
  if (!mapping) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-yellow-50">
        <div className="text-center p-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Page Not Found</h1>
          <p className="text-gray-600 mb-6">No gold price data found for this URL.</p>
          <a href="/" className="bg-yellow-600 text-white px-6 py-3 rounded-lg hover:bg-yellow-700 transition-colors">
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  const { country, currency, flag, region } = mapping;

  // ── Single localRate used everywhere — live API rate takes priority over fallback ──
  // Both API and FALLBACK_RATES store rates as "X local units per 1 USD"
  // e.g. INR: 83.5 means multiply goldUSD × 83.5 to get INR price
  const localRate       = exchangeRates[currency] || FALLBACK_RATES[currency] || 1;
  const price24kPerGram = goldPerGramUSD * (1/localRate);
  const price24kPerOz   = goldPerGramUSD * UNITS.toz.multiplier * (1/localRate);

  const fmt = (n, dec = 2) => n.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec });

  return (
    <>
      <SEOHead mapping={mapping} goldPerGramUSD={goldPerGramUSD} />

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50">
        <Navbar />

        {/* ── Hero ── */}
        <div className="bg-gradient-to-r from-yellow-600 to-amber-600 text-white py-12 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-3">
              <span className="text-5xl">{flag}</span>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold leading-tight">
                  Gold Price in {country} Today ({currency})
                </h1>
                <p className="text-yellow-100 text-sm mt-1">
                  Live gold rate in {currency} — updated every 5 minutes
                </p>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center gap-3 mt-4">
                <RefreshCw className="w-6 h-6 animate-spin" />
                <span>Fetching live price…</span>
              </div>
            ) : (
              <div className="mt-4 flex flex-wrap items-end gap-8">
                <div>
                  <p className="text-yellow-200 text-xs mb-1">24K Gold — per Gram</p>
                  <p className="text-4xl md:text-5xl font-black">
                    {fmt(price24kPerGram)}
                    <span className="text-2xl ml-2 font-normal">{currency}</span>
                  </p>
                </div>
                <div>
                  <p className="text-yellow-200 text-xs mb-1">24K Gold — per Troy Oz</p>
                  <p className="text-2xl font-bold opacity-90">
                    {fmt(price24kPerOz)}
                    <span className="text-base ml-1 font-normal">{currency}</span>
                  </p>
                </div>
                <div className={`flex items-center gap-2 text-lg font-semibold ${change >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  {change >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                  {change >= 0 ? '+' : ''}{change}% today
                </div>
              </div>
            )}

            {lastUpdated && (
              <p className="text-yellow-200 text-xs mt-3">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="max-w-5xl mx-auto px-6 py-8">
          <Breadcrumb country={country} />

          

          <div className="grid lg:grid-cols-3 gap-6">

            {/* ── Main column ── */}
            <div className="lg:col-span-2 space-y-6">

              {/* Table 1 — Gram */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                  <h2 className="font-bold text-gray-800 text-lg">
                    Gold Rate in {country} — All Karats (per Gram)
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">Prices in {currency} per 1 gram</p>
                </div>
                <PriceTable goldPerGramUSD={goldPerGramUSD} localRate={localRate} currency={currency} unitKey="g" />
              </div>

		{/* Table 1 A — 10 Gram */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                  <h2 className="font-bold text-gray-800 text-lg">
                    Gold Rate in {country} — All Karats (per 10 Gram)
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">Prices in {currency} per 10 gram</p>
                </div>
                <PriceTable goldPerGramUSD={goldPerGramUSD} localRate={localRate} currency={currency} unitKey="teng" />
              </div>


              {/* Table 2 — Troy Ounce */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                  <h2 className="font-bold text-gray-800 text-lg">
                    Gold Rate in {country} — All Karats (per Troy Ounce)
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">Prices in {currency} per troy ounce (31.1035 g)</p>
                </div>
                <PriceTable goldPerGramUSD={goldPerGramUSD} localRate={localRate} currency={currency} unitKey="toz" />
              </div>

 

              {/* Table 3 — Kilogram */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                  <h2 className="font-bold text-gray-800 text-lg">
                    Gold Rate in {country} — All Karats (per Kilogram)
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">Prices in {currency} per kilogram (1,000 g)</p>
                </div>
                <PriceTable goldPerGramUSD={goldPerGramUSD} localRate={localRate} currency={currency} unitKey="kg" />
              </div>

              {/* Table 4 — Metric Tonne */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-5 border-b border-gray-100">
                  <h2 className="font-bold text-gray-800 text-lg">
                    Gold Rate in {country} — All Karats (per Metric Tonne)
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">Prices in {currency} per metric tonne (1,000,000 g)</p>
                </div>
                <PriceTable goldPerGramUSD={goldPerGramUSD} localRate={localRate} currency={currency} unitKey="mt" />
              </div>

              {/* Disclaimer */}
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4 flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900">
                  <strong>Disclaimer:</strong> Prices shown are indicative, based on the international spot
                  price converted to {currency}. Actual buying/selling prices in {country} may vary due to
                  local premiums, taxes, and dealer margins.
                </p>
              </div>

              

              {/* SEO article */}
              <div className="bg-white rounded-xl shadow-lg p-6 text-gray-700 space-y-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Gold Price in {country} — Live {currency} Gold Rate
                </h2>
                <p>
                  Welcome to LivePriceForGold.com, your source for the live gold price in {country} today.
                  We display the current gold rate in {currency} for all karat purities — 24K, 22K, 21K, 18K,
                  14K, 10K, and 8K — across four weight units: Gram, Troy Ounce, Kilogram, and Metric Tonne.
                  All four tables are derived from a single live API fetch, so prices are always consistent.
                </p>
                <h3 className="text-lg font-bold text-gray-800">How to Read the Gold Rate in {currency}</h3>
                <p>
                  Gold is priced globally in US Dollars per troy ounce. The {currency} gold price you see
                  here is calculated by multiplying the USD spot price per gram by the current USD/{currency}
                  exchange rate, then scaled to your chosen unit. Both rates update every 5 minutes.
                </p>
                <h3 className="text-lg font-bold text-gray-800">Gold Karat Explained</h3>
                <p>
                  A karat (K) measures gold purity. 24K gold is 99.9% pure and commands the highest price.
                  22K (91.67% pure) is popular for jewelry across {region}. 18K (75% pure) balances
                  durability and value, widely used in {country}.
                </p>
                <h3 className="text-lg font-bold text-gray-800">Why Check Gold Price in {country}?</h3>
                <p>
                  Whether you're buying jewelry, investing, or selling gold in {country}, knowing the
                  current {currency} gold rate helps you transact at a fair market price. Always verify
                  with local dealers as retail prices may include making charges and taxes.
                </p>
              </div>

              <FAQ country={country} currency={currency} />
            </div>

            {/* ── Right Sidebar ── */}
            <div className="space-y-6">

              {/* Quick Stats — all derived from same goldPerGramUSD */}
              <div className="bg-white rounded-xl shadow-lg p-5">
                <h3 className="font-bold text-gray-800 mb-4">Quick Stats</h3>
                <div className="space-y-3 text-sm">
                  {[
                    ['24K / Gram',     goldPerGramUSD * (1/localRate)],
                    ['22K / Gram',     goldPerGramUSD * (1/localRate) * 0.9167],
                    ['18K / Gram',     goldPerGramUSD * (1/localRate) * 0.75],
                    ['24K / Troy Oz',  goldPerGramUSD * UNITS.toz.multiplier * (1/localRate)],
                    ['24K / KG',       goldPerGramUSD * UNITS.kg.multiplier  * (1/localRate)],
                  ].map(([label, val]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-gray-500">{label}</span>
                      <span className="font-semibold">{fmt(val)} {currency}</span>
                    </div>
                  ))}
                  <div className="border-t border-gray-100 pt-3 flex justify-between">
                    <span className="text-gray-500">USD Spot / Gram</span>
                    <span className="font-semibold">${fmt(goldPerGramUSD)}</span>
                  </div>
                </div>
              </div>

               

              <RelatedCountries currentSlug={slug} region={region} />

              <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-yellow-600" />
                  All Countries
                </h3>
                <p className="text-sm text-gray-600 mb-3">See gold prices in 140+ countries and currencies.</p>
                <a href="/" className="bg-yellow-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-yellow-700 transition-colors inline-flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  View All Rates
                </a>
              </div>

            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
